import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ApprovalFinancialSummaryProps {
  estimatedCost: number;
  budgetAvailable: number;
  budgetCommitted: number;
  budgetRemaining: number;
  budgetUtilizationPercentage: number;
  budgetStatus: 'AVAILABLE' | 'WARNING' | 'EXCEEDED';
  currency?: string;
  costCenter: string;
  companyCode: string;
}

export const ApprovalFinancialSummary: React.FC<ApprovalFinancialSummaryProps> = ({
  estimatedCost,
  budgetAvailable,
  budgetCommitted,
  budgetRemaining,
  budgetUtilizationPercentage,
  budgetStatus,
  costCenter,
  companyCode,
}) => {
  const isExceeded = budgetStatus === 'EXCEEDED' || budgetRemaining < 0;

  const formatInr = (val: number) =>
    `₹${Number(val || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  return (
    <Card className="border-border rounded-2xl shadow-2xs font-poppins">
      <CardHeader className="pb-3 border-b border-border/60">
        <div className="flex items-center justify-between">
          <CardTitle className="font-poppins text-base font-bold text-foreground flex items-center gap-2.5">
            <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-extrabold text-sm">
              ₹
            </div>
            Financial & Cost Center Budget Authorization Summary
          </CardTitle>
          <span
            className={cn(
              'text-xs font-bold px-2.5 py-1 rounded-full border font-poppins',
              isExceeded
                ? 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/20'
                : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20'
            )}
          >
            {isExceeded ? '⚠️ BUDGET EXCEEDED' : '✓ BUDGET AVAILABLE'}
          </span>
        </div>
      </CardHeader>

      <CardContent className="pt-4 space-y-4 font-poppins">
        {/* Cost Center Badges */}
        <div className="flex items-center gap-3 text-xs text-foreground-secondary font-semibold">
          <span>Cost Center: <strong className="text-foreground">{costCenter}</strong></span>
          <span>•</span>
          <span>Company Code: <strong className="text-foreground">{companyCode}</strong></span>
          <span>•</span>
          <span>Currency: <strong className="text-foreground">INR (₹)</strong></span>
        </div>

        {/* 4 Financial Metric Boxes */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="rounded-xl border border-border p-3 bg-card space-y-1">
            <span className="text-[11px] font-bold text-foreground-muted uppercase">This Requisition</span>
            <span className="font-poppins text-lg font-bold text-primary block">
              {formatInr(estimatedCost)}
            </span>
          </div>

          <div className="rounded-xl border border-border p-3 bg-card space-y-1">
            <span className="text-[11px] font-bold text-foreground-muted uppercase">Annual Budget</span>
            <span className="font-poppins text-lg font-bold text-foreground block">
              {formatInr(budgetAvailable)}
            </span>
          </div>

          <div className="rounded-xl border border-border p-3 bg-card space-y-1">
            <span className="text-[11px] font-bold text-foreground-muted uppercase">Committed Spend</span>
            <span className="font-poppins text-lg font-bold text-foreground-secondary block">
              {formatInr(budgetCommitted)}
            </span>
          </div>

          <div
            className={cn(
              'rounded-xl border p-3 space-y-1',
              isExceeded ? 'bg-rose-500/10 border-rose-500/30' : 'bg-emerald-500/10 border-emerald-500/30'
            )}
          >
            <span className="text-[11px] font-bold text-foreground-muted uppercase">Remaining Buffer</span>
            <span
              className={cn(
                'font-poppins text-lg font-bold block',
                isExceeded ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-700 dark:text-emerald-300'
              )}
            >
              {formatInr(budgetRemaining)}
            </span>
          </div>
        </div>

        {/* Budget Utilization Progress Bar */}
        <div className="space-y-1.5 pt-1">
          <div className="flex justify-between text-xs font-bold">
            <span className="text-foreground-secondary">Budget Utilization</span>
            <span className={isExceeded ? 'text-rose-600' : 'text-primary'}>
              {Math.round(budgetUtilizationPercentage)}% Allocated
            </span>
          </div>

          <div className="h-2.5 w-full rounded-full bg-muted overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-500',
                isExceeded ? 'bg-rose-500' : 'bg-primary'
              )}
              style={{ width: `${Math.min(budgetUtilizationPercentage, 100)}%` }}
            />
          </div>
        </div>

        {/* Budget Warning Banner if Exceeded */}
        {isExceeded && (
          <div className="flex items-start gap-3 rounded-xl border border-rose-500/30 bg-rose-500/10 p-3.5 text-xs text-rose-700 dark:text-rose-300">
            <AlertTriangle className="h-4.5 w-4.5 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">Insufficient Cost Center Budget Balance</p>
              <p className="mt-0.5 text-rose-600 dark:text-rose-400">
                This request exceeds the available budget buffer by {formatInr(Math.abs(budgetRemaining))}. Executive finance exception sign-off is required.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
